import type { ReactNode } from 'react';
import { CaretDownIcon, CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { Button, cn } from '@skupreme/shared/ui';
import {
  AppTableCell,
  AppTableRow,
  type AppTableCellProps,
  type AppTableRowProps,
} from '@components/AppTable';
import { BoldText, MuteText } from '@elements/Common';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { CommonUtility } from '@utility/common';

/*
 * The pieces the PO item and component rows share: the PO detail, create,
 * bulk and public supplier screens all render those rows.
 */

export function RowLine({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'sk:mb-1.25 sk:flex sk:flex-wrap sk:items-center sk:justify-between sk:gap-x-2',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SmallMuteText({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <MuteText className={cn('sk:text-[11px]', className)}>{children}</MuteText>
  );
}

/** Text that acts, such as "Set Cost", without a button's chrome. */
export function TextAction({
  onClick,
  title,
  className,
  children,
}: {
  onClick?: () => void;
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'sk:m-0 sk:cursor-pointer sk:appearance-none sk:border-0 sk:bg-transparent sk:p-0 sk:font-sans sk:text-inherit',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function ExpandToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <TextAction onClick={onToggle} className="sk:flex sk:items-center sk:gap-1">
      <BoldText>{expanded ? 'Collapse' : 'Expand'}</BoldText>
      {expanded ? (
        <CaretDownIcon size={12} aria-hidden />
      ) : (
        <CaretRightIcon size={12} aria-hidden />
      )}
    </TextAction>
  );
}

export function AddAliasButton({ onPress }: { onPress: () => void }) {
  return (
    <Button size="sm" onPress={onPress}>
      <PlusIcon aria-hidden />
      Alias
    </Button>
  );
}

type QtyInputProps = {
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  /** Flags the cell, as an MOQ the quantity falls short of does. */
  isInvalid?: boolean;
  maxValue: number;
};

/** The carton quantity a row is ordered in. */
export function CartonQtyInput({
  value,
  onChange,
  isInvalid,
  maxValue,
}: QtyInputProps) {
  return (
    <div className="sk:mx-auto sk:w-17.5">
      <NumberMaskInput
        aria-label="Carton quantity"
        aria-invalid={isInvalid || undefined}
        value={value}
        onChange={onChange}
        className={cn(isInvalid && 'sk:ring-1 sk:ring-danger sk:bg-danger-bg!')}
        maskOptions={{
          includeThousandsSeparator: true,
          thousandsSeparatorSymbol: ',',
        }}
        maxValue={maxValue}
      />
    </div>
  );
}

/** The cell holding `CartonQtyInput`: tall enough that an MOQ message does not reflow the row. */
export function CartonQtyCell({ className, ...props }: AppTableCellProps) {
  return (
    <AppTableCell {...props} className={cn('sk:relative sk:h-40', className)} />
  );
}

export function QtyErrorMessage({ children }: { children: ReactNode }) {
  return (
    <div className="sk:mt-1.25 sk:text-xs sk:leading-4 sk:text-danger">
      {children}
    </div>
  );
}

/** A row under its parent for one carton alias; its edit controls show on hover. */
export function AliasTableRow({ className, ...props }: AppTableRowProps) {
  return (
    <AppTableRow
      {...props}
      className={cn(
        'action-hover sk:group/alias sk:bg-fill-tertiary',
        'sk:[&>td]:h-auto sk:[&>td]:px-4! sk:[&>td]:py-2!',
        className,
      )}
    />
  );
}

/** Only shown while the alias row is hovered or one of them has focus. */
export const ALIAS_ACTION =
  'sk:opacity-0 sk:transition-opacity sk:group-hover/alias:opacity-100 sk:focus-visible:opacity-100';

export function AliasActions({ children }: { children: ReactNode }) {
  return (
    <div className="sk:absolute sk:top-1/2 sk:right-5 sk:flex sk:-translate-y-1/2 sk:items-center sk:gap-2">
      {children}
    </div>
  );
}

type IPrice = { price: number; moq: number };

/** The item's cost, or every tier with the one in force picked out. */
export function ItemPrice(
  readonly: boolean | undefined,
  prices: IPrice[] | undefined,
  current_price: number,
  currency?: string,
): ReactNode {
  const tiers = prices || [];
  if (readonly) {
    return CommonUtility.currencyFormat(current_price, currency, undefined, 4);
  }
  if (tiers.length === 1) {
    return CommonUtility.currencyFormat(tiers[0].price, currency, undefined, 4);
  }

  return tiers.map(price => (
    <div
      key={`MOQ-${price.moq}`}
      className={cn(
        'sk:mx-auto sk:flex sk:max-w-32.5 sk:flex-wrap sk:items-center sk:justify-between sk:gap-x-2',
        price.price === current_price
          ? 'sk:font-bold sk:text-fg'
          : 'sk:text-fg-tertiary',
      )}
    >
      <span>{price.moq <= 1 ? 'MOQ' : `> ${price.moq}`}</span>
      <span>
        {CommonUtility.currencyFormat(price.price, currency, undefined, 4)}
      </span>
    </div>
  ));
}
